import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { AGUSegmentOption, AGUSelectOption } from 'agu_app_shell';
import { ActiveFilterPill } from '../../finder/active-filter-pills';
import { ToSelectOptions, WithAll } from '../../finder/select-options';
import { ResearchJobFacets, ResearchJobFilterInput, UnderstoodJobConstraint } from '../../models/research.models';

/** Client-only Region toggle: not a `ResearchJobFilterInput` field on its own — the page combines it with the Country dropdown into `country`/`countryNot` at request time (see `JobFinderPageComponent.ActiveFiltersForServer`). */
export type JobRegion = 'all' | 'us' | 'international';

/** Client-only Work location toggle: combined into the real `remote` wire field at request time. */
export type JobWorkLocation = 'all' | 'remote' | 'onsite';

const REGION_OPTIONS: ReadonlyArray<{ value: JobRegion; label: string }> = [
    { value: 'all', label: 'All' },
    { value: 'us', label: 'U.S.' },
    { value: 'international', label: 'International' },
];

const WORK_LOCATION_OPTIONS: ReadonlyArray<{ value: JobWorkLocation; label: string }> = [
    { value: 'all', label: 'All' },
    { value: 'remote', label: 'Remote' },
    { value: 'onsite', label: 'On-site' },
];

type SalaryOption = '' | 'lt80' | '80-100' | '100-120' | '120plus';

const SALARY_OPTIONS: ReadonlyArray<{ value: SalaryOption; label: string }> = [
    { value: '', label: 'Any salary' },
    { value: 'lt80', label: 'Under $80K' },
    { value: '80-100', label: '$80K to $100K' },
    { value: '100-120', label: '$100K to $120K' },
    { value: '120plus', label: '$120K+' },
];

/**
 * Salary bucket → `ResearchJobFilterInput.salaryMinUSD`/`salaryMaxUSD`.
 * `salaryMaxUSD` matches jobs whose salary MIN is ≤ this (an upper bound on
 * what the role pays at the low end); `salaryMinUSD` matches jobs whose
 * salary MAX is ≥ this (a floor on what the role pays at the high end) — see
 * the field doc comments on `ResearchJobFilterInput`. "$80K to $100K" is an
 * overlap test (both bounds set), matching the mockup's own bucket logic
 * (`j.salMax < b[0] || j.sal >= b[1]` in the bundled Jobs screen JS).
 */
const SALARY_RANGES: Readonly<Record<Exclude<SalaryOption, ''>, { min?: number; max?: number }>> = {
    lt80: { max: 80_000 },
    '80-100': { min: 80_000, max: 100_000 },
    '100-120': { min: 100_000, max: 120_000 },
    '120plus': { min: 120_000 },
};

/**
 * UI-question-level active-filter count for the toolbar's `ActiveFilterCount`
 * badge — mirrors `CountActiveGrantFilterGroups`/`CountActivePeopleFilterGroups`:
 * one bump per answered filter QUESTION, including the three client-only
 * toggles (`region`, `workLocation`, `postedThisWeek`) since each of those
 * genuinely narrows the request sent to the server, just via a different
 * wire field than the one it's named after. `ShowSavedOnly`/`RecommendedOnly`
 * are excluded, same as the other two finders' Show/Recommended toggles.
 */
export function CountActiveJobFilterGroups(f: ResearchJobFilterInput, region: JobRegion, workLocation: JobWorkLocation, postedThisWeek: boolean): number {
    let count = 0;
    if (workLocation !== 'all') count++;
    if (region !== 'all') count++;
    if (f.industry && f.industry.length > 0) count++;
    if (f.section && f.section.length > 0) count++;
    if (f.state && f.state.length > 0) count++;
    if (f.country && f.country.length > 0) count++;
    if (f.salaryMinUSD !== undefined && f.salaryMinUSD !== null) count++;
    else if (f.salaryMaxUSD !== undefined && f.salaryMaxUSD !== null) count++;
    if (f.recommendedOnly) count++;
    if (postedThisWeek) count++;
    // Groups the rail has no control for but an understood REQUIREMENT can set
    // (they live in the filters the echo wrote back — never a preference, which
    // stays out of the filters and therefore out of this count).
    if (f.seniority && f.seniority.length > 0) count++;
    if (f.employmentType && f.employmentType.length > 0) count++;
    if (f.companySize && f.companySize.length > 0) count++;
    if (f.city && f.city.length > 0) count++;
    if (region === 'all' && f.countryNot && f.countryNot.length > 0) count++;
    if (!postedThisWeek && f.postedAfter !== undefined && f.postedAfter !== null) count++;
    return count;
}

/** Whether the rail's "More filters" fold holds a set filter — the salary range (`state.sal || state.visa`, P/job-finder.html:1517; there is no visa filter in this build). */
export function JobFoldHasFilters(f: ResearchJobFilterInput): boolean {
    return (f.salaryMinUSD !== undefined && f.salaryMinUSD !== null) || (f.salaryMaxUSD !== undefined && f.salaryMaxUSD !== null);
}

/**
 * Query group → the `ResearchJobFilterInput` fields it owns, mirroring
 * `JOB_GROUP_FIELDS` in `apps/MJAPI/src/resolvers/research/jobIntake.ts`. A fresh
 * submit strips the PREVIOUS query's groups through this map (`StripQueryGroups`),
 * so manual picks survive and stale query filters do not.
 */
export const JOB_QUERY_GROUP_FIELDS: Readonly<Record<string, ReadonlyArray<keyof ResearchJobFilterInput>>> = {
    country: ['country'],
    excludeCountry: ['countryNot'],
    salary: ['salaryMinUSD', 'salaryMaxUSD'],
    posted: ['postedAfter'],
    state: ['state'],
    city: ['city'],
    employmentType: ['employmentType'],
    seniority: ['seniority'],
    companySize: ['companySize'],
    section: ['section'],
    industry: ['industry'],
    company: [],
};

/** The pill/group key a constraint kind belongs to — both salary bounds share one `Salary` pill and one group. */
export function JobPillGroupOf(kind: string): string {
    return kind === 'salaryMin' || kind === 'salaryMax' ? 'salary' : kind;
}

/** The nine corpus countries (spec §3.1) — a pill shows the member a name, never an ISO code. */
const JOB_COUNTRY_NAMES: Readonly<Record<string, string>> = {
    us: 'United States', gb: 'United Kingdom', ca: 'Canada', in: 'India', au: 'Australia',
    za: 'South Africa', sg: 'Singapore', ie: 'Ireland', nz: 'New Zealand',
};

export function JobCountryName(code: string): string {
    return JOB_COUNTRY_NAMES[code.toLowerCase()] ?? code.toUpperCase();
}

/** The rail's Country options: the facet's ISO codes as keys, the member-facing name as each label. */
export function JobCountrySelectOptions(codes: readonly string[]): AGUSelectOption[] {
    return [{ Key: '', Label: 'All countries' }, ...codes.map((c) => ({ Key: c, Label: JobCountryName(c) }))];
}

function FormatUsdK(n: number): string {
    return `$${Math.round(n / 1000)}K`;
}

/**
 * "small company" is a RANGE of feed buckets (spec §3.1): three buckets on one
 * pill would read `1-10 employees, 11-50 employees, 51-200 employees`, so a set
 * of buckets collapses to its span — `1–200 employees`, `1,001+ employees`. A
 * single bucket, or one the parser does not recognise, is shown as delivered.
 */
export function FormatCompanySizePill(values: readonly string[]): string {
    if (values.length <= 1) return values.join(', ');
    let low: number | null = null;
    let high: number | null = null;
    let openEnded = false;
    for (const v of values) {
        if (v === 'Myself Only') { low = low === null ? 1 : Math.min(low, 1); high = high === null ? 1 : Math.max(high, 1); continue; }
        const m = /^([\d,]+)(?:-([\d,]+)|\+)\s*employees$/.exec(v);
        if (m === null) return values.join(', ');
        const a = Number(m[1].replace(/,/g, ''));
        low = low === null ? a : Math.min(low, a);
        if (m[2] === undefined) { openEnded = true; continue; }
        const b = Number(m[2].replace(/,/g, ''));
        high = high === null ? b : Math.max(high, b);
    }
    if (low === null) return values.join(', ');
    const fmt = (n: number): string => n.toLocaleString('en-US');
    if (openEnded) return `${fmt(low)}+ employees`;
    return `${fmt(low)}\u2013${fmt(high ?? low)} employees`;
}

export function SalaryOptionLabelFor(f: ResearchJobFilterInput): string | null {
    for (const option of SALARY_OPTIONS) {
        if (option.value === '') continue;
        const range = SALARY_RANGES[option.value];
        if ((range.min ?? undefined) === (f.salaryMinUSD ?? undefined) && (range.max ?? undefined) === (f.salaryMaxUSD ?? undefined)) return option.label;
    }
    return null;
}

const JOB_PILL_LABEL: Readonly<Record<string, string>> = {
    country: 'Location', state: 'Location', city: 'Location',
    excludeCountry: 'Excluding',
    employmentType: 'Type', seniority: 'Seniority', companySize: 'Company size',
    section: 'Science area', industry: 'Industry', company: 'Employer',
    salary: 'Salary', posted: 'Posted',
};

const PREFERRED_SUFFIX = ' · preferred';

export function BuildJobFilterPills(
    filters: ResearchJobFilterInput,
    constraints: readonly UnderstoodJobConstraint[]
): ActiveFilterPill[];
export function BuildJobFilterPills(
    filters: ResearchJobFilterInput,
    region: JobRegion,
    workLocation: JobWorkLocation,
    postedThisWeek: boolean
): ActiveFilterPill[];
export function BuildJobFilterPills(
    filters: ResearchJobFilterInput,
    arg2?: readonly UnderstoodJobConstraint[] | JobRegion,
    arg3?: JobWorkLocation,
    arg4?: boolean
): ActiveFilterPill[] {
    if (Array.isArray(arg2)) {
        const pills: ActiveFilterPill[] = [];
        // Both salary bounds fold into ONE `Salary` pill ("$80K to $100K"), keyed by
        // the group so removing it clears both fields.
        const salaryMin = arg2.find((c) => c.kind === 'salaryMin');
        const salaryMax = arg2.find((c) => c.kind === 'salaryMax');
        for (const c of arg2) {
            const group = JobPillGroupOf(c.kind);
            const label = JOB_PILL_LABEL[group];
            if (label === undefined) continue;
            if (pills.some((p) => p.Key === group)) continue;
            let value: string;
            if (group === 'salary') {
                const min = salaryMin ? Number(salaryMin.values[0]) : null;
                const max = salaryMax ? Number(salaryMax.values[0]) : null;
                value = min !== null && max !== null ? `${FormatUsdK(min)} to ${FormatUsdK(max)}` : min !== null ? `From ${FormatUsdK(min)}` : `Up to ${FormatUsdK(max ?? 0)}`;
            } else if (group === 'posted') {
                value = `within ${c.values[0]} days`;
            } else if (group === 'country' || group === 'excludeCountry') {
                value = c.values.map(JobCountryName).join(', ');
            } else if (group === 'companySize') {
                value = FormatCompanySizePill(c.values);
            } else {
                value = c.values.join(', ');
            }
            pills.push({ Key: group, Label: label, Value: value + (c.stance === 'preference' ? PREFERRED_SUFFIX : ''), ...((filters.fromQuery ?? []).includes(group) ? { FromQuery: true } : {}) });
        }
        return pills;
    }
    // Backward-compatible implementation for (filters, region, workLocation, postedThisWeek)
    const region = (typeof arg2 === 'string' ? arg2 : 'all') as JobRegion;
    const workLocation = arg3 ?? 'all';
    const postedThisWeek = arg4 ?? false;

    const pills: ActiveFilterPill[] = [];
    if (workLocation !== 'all') pills.push({ Key: 'workLocation', Label: 'Work location', Value: WORK_LOCATION_OPTIONS.find((o) => o.value === workLocation)?.label ?? workLocation });
    if (region !== 'all') pills.push({ Key: 'region', Label: 'Region', Value: REGION_OPTIONS.find((o) => o.value === region)?.label ?? region });
    if (filters.industry && filters.industry.length > 0) pills.push({ Key: 'industry', Label: 'Sector', Value: filters.industry.join(', ') });
    if (filters.section && filters.section.length > 0) pills.push({ Key: 'section', Label: 'Science area', Value: filters.section.join(', ') });
    if (filters.state && filters.state.length > 0) pills.push({ Key: 'state', Label: 'State', Value: filters.state.join(', ') });
    if (filters.country && filters.country.length > 0) pills.push({ Key: 'country', Label: 'Country', Value: filters.country.map(JobCountryName).join(', ') });
    const salary = SalaryOptionLabelFor(filters);
    if (salary) pills.push({ Key: 'salary', Label: 'Salary', Value: salary });
    if (postedThisWeek) pills.push({ Key: 'postedThisWeek', Label: 'Posted', Value: 'Past 7 days' });
    if (filters.recommendedOnly) pills.push({ Key: 'recommendedOnly', Label: 'Show', Value: 'Recommended only' });
    return pills;
}

export function RemoveJobFilterGroup(f: ResearchJobFilterInput, key: string): ResearchJobFilterInput {
    if (key === 'recommendedOnly') return { ...f, recommendedOnly: false };
    const fields = JOB_QUERY_GROUP_FIELDS[key];
    if (fields === undefined) return f;
    const out: ResearchJobFilterInput = { ...f };
    for (const field of fields) {
        // Every group field is either a string[] or a scalar; the empty shape is the one `EMPTY_JOB_FILTERS` uses.
        (out as Record<string, unknown>)[field] = Array.isArray(f[field]) ? [] : undefined;
    }
    // The group no longer came from the query once its pill is gone (`fromQuery` is preserved for the rest — contract §5).
    if (f.fromQuery && f.fromQuery.length > 0) out.fromQuery = f.fromQuery.filter((g) => g !== key);
    return out;
}

/** The location groups a Region hides: State shows only for the U.S., Country only for International (P/job-finder.html:1325-1326). */
export function RegionHiddenGroups(region: JobRegion): ReadonlyArray<'state' | 'country'> {
    if (region === 'us') return ['country'];
    if (region === 'international') return ['state'];
    return ['state', 'country'];
}

/**
 * Clears the State/Country values the new Region hides, so a Region change never leaves an active filter the rail no longer shows
 * (L79). Each cleared group also leaves `fromQuery`. The same object comes back when nothing hidden is set.
 */
export function ClearRegionHiddenFilters(f: ResearchJobFilterInput, region: JobRegion): ResearchJobFilterInput {
    let out = f;
    for (const group of RegionHiddenGroups(region)) {
        if ((out[group]?.length ?? 0) > 0) out = RemoveJobFilterGroup(out, group);
    }
    return out;
}

/**
 * Job Finder's filter panel — projected into `ra-finder-toolbar`'s `[filters]`
 * slot. Two-way binds `Filters` (`ResearchJobFilterInput`, the wire shape
 * sent to `researchJobPortalSearch`) plus the module's client-only fields:
 * `Region`/`WorkLocation` (each a 3-way toggle with no single wire field of
 * its own — the page combines them with `Filters.country` into
 * `country`/`countryNot`/`remote` at request time) and `PostedThisWeek`
 * (combined into `postedAfter`), alongside `ShowSavedOnly`/`RecommendedOnly`
 * (pure client toggles, same as Grant/Collaborator Finder).
 *
 * The Salary-range radio group tracks its own selection locally, same as
 * Grant Finder's Award-amount radio (`ngOnChanges` only resets it back to
 * "Any" when both wire fields disappear from `Filters` — the one external-
 * reset case, a "Clear all" — since a bucket key can't be inverted back out
 * of a min/max pair).
 */
@Component({
    standalone: false,
    selector: 'ra-job-filters',
    templateUrl: './job-filters.component.html',
})
export class JobFiltersComponent implements OnChanges {
    @Input() Filters: ResearchJobFilterInput = {};
    @Output() FiltersChange = new EventEmitter<ResearchJobFilterInput>();
    @Input() Facets: ResearchJobFacets | null = null;

    /** Drives `agu-rail-fold`'s auto-open (P/job-finder.html:1517). */
    get FoldHasFilters(): boolean {
        return JobFoldHasFilters(this.Filters);
    }

    @Input() Region: JobRegion = 'all';
    @Output() RegionChange = new EventEmitter<JobRegion>();

    @Input() WorkLocation: JobWorkLocation = 'all';
    @Output() WorkLocationChange = new EventEmitter<JobWorkLocation>();

    /** Client-only: combined into `postedAfter` (today − 7 days) by the page. Not sent to the server as-is. */
    @Input() PostedThisWeek = false;
    @Output() PostedThisWeekChange = new EventEmitter<boolean>();

    /** Client-only: filters the already-fetched list to `itemState.saved` items. Not sent to the server. */
    @Input() ShowSavedOnly = false;
    @Output() ShowSavedOnlyChange = new EventEmitter<boolean>();

    /** Mirrors `state.tab === 'recommended'`; toggling it is an alternate path to the same tab switch the toolbar's tab-nav performs. */
    @Input() RecommendedOnly = false;
    @Output() RecommendedOnlyChange = new EventEmitter<boolean>();

    readonly WorkLocationSegments: AGUSegmentOption[] = WORK_LOCATION_OPTIONS.map((o) => ({ Key: o.value, Label: o.label }));
    readonly RegionSegments: AGUSegmentOption[] = REGION_OPTIONS.map((o) => ({ Key: o.value, Label: o.label }));
    readonly SalarySelect = ToSelectOptions(SALARY_OPTIONS);

    get SectorSelect(): AGUSelectOption[] { return WithAll('All sectors', this.Facets?.industries ?? []); }
    get AreaSelect(): AGUSelectOption[] { return WithAll('All science areas', this.Facets?.sections ?? []); }
    get StateSelect(): AGUSelectOption[] { return WithAll('All states', this.Facets?.states ?? []); }
    get CountrySelect(): AGUSelectOption[] { return JobCountrySelectOptions(this.Facets?.countries ?? []); }

    SalaryValue: SalaryOption = '';

    ngOnChanges(): void {
        if (this.Filters.salaryMinUSD === undefined && this.Filters.salaryMaxUSD === undefined) {
            this.SalaryValue = '';
        }
    }

    get IndustryValue(): string { return this.Filters.industry?.[0] ?? ''; }
    OnIndustryChange(value: string): void { this.Emit({ ...this.Filters, industry: value ? [value] : [] }); }

    get SectionValue(): string { return this.Filters.section?.[0] ?? ''; }
    OnSectionChange(value: string): void { this.Emit({ ...this.Filters, section: value ? [value] : [] }); }

    get StateValue(): string { return this.Filters.state?.[0] ?? ''; }
    OnStateChange(value: string): void { this.Emit({ ...this.Filters, state: value ? [value] : [] }); }

    get CountryValue(): string { return this.Filters.country?.[0] ?? ''; }
    OnCountryChange(value: string): void { this.Emit({ ...this.Filters, country: value ? [value] : [] }); }

    OnWorkLocationSegment(value: string): void {
        if (value === 'all' || value === 'remote' || value === 'onsite') this.WorkLocationChange.emit(value);
    }
    OnRegionSegment(value: string): void {
        if (value === 'all' || value === 'us' || value === 'international') this.RegionChange.emit(value);
    }
    OnSalarySelect(value: string): void {
        if (value === '' || value === 'lt80' || value === '80-100' || value === '100-120' || value === '120plus') this.OnSalaryChange(value);
    }

    OnSalaryChange(value: SalaryOption): void {
        this.SalaryValue = value;
        const picked = value ? SALARY_RANGES[value] : {};
        this.Emit({ ...this.Filters, salaryMinUSD: picked.min, salaryMaxUSD: picked.max });
    }

    private Emit(next: ResearchJobFilterInput): void {
        this.FiltersChange.emit(next);
    }
}
